import { App, Empty, Skeleton } from "antd"
import React, { useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { type ConnectedProps, connect } from "react-redux"
import { useNavigate, useParams } from "react-router"

import { type RootState } from "../../../store.ts"
import {
  copyPhaseForProjectType,
  createPhaseForProjectType,
  deletePhaseForProjectType
} from "../redux/templates-phases.thunks.ts"
import {
  deleteProjectType,
  fetchProjectTypeDetails,
  updateProjectTypeAndPhase
} from "../redux/templates-project-types.thunks.ts"
import { setTemplatesProp } from "../redux/templates.slice.ts"
import type {
  ProjectTypeAddPhaseFormData,
  ProjectTypeTemplatePhase,
  UpdateProjectTypeAndPhaseParams
} from "../types/project-type.types.ts"
import {
  ProjectType,
  type ProjectTypeFormData
} from "./components/ProjectType.tsx"
import { TextInput } from "../../../components/Input/TextInput.tsx"

const ProjectTypePage = ({
  fetching,
  permissions,
  saving,
  selectedPhase,
  projectType,
  fetchProjectType,
  updateProjectTypeAndPhase,
  deleteProjectType,
  deletePhase,
  copyPhase,
  createPhase,
  setProp
}: ConnectedProps<typeof connector>) => {
  const { modal } = App.useApp()
  const id = useParams()?.id as string
  const { t } = useTranslation()
  const navigate = useNavigate()

  const sortedPhases = useMemo(() => {
    if (!projectType?.phases?.length) return []
    return [...(projectType?.phases || [])]?.sort((a, b) => a?.order - b?.order)
  }, [projectType?.phases])

  useEffect(() => {
    if (!id) setProp({ key: "projectTypeDetails", value: null })
    fetchProjectType(id)
  }, [id])

  if (fetching) {
    return <Skeleton.Node className={"size-full"} />
  }

  if (!projectType) {
    return <Empty description={"Project type not found"} />
  }

  const handleDeleteProjectType = () => {
    modal.confirm({
      centered: true,
      title: t("Confirm deleting"),
      content: t(
        "Are you sure you want to delete this project type? This action can NOT be undone."
      ),

      cancelText: t("Cancel"),
      okText: t("Delete"),
      okButtonProps: {
        danger: true,
        ghost: true
      },
      onOk() {
        deleteProjectType({ id, navigate })
      }
    })
  }

  const handleSubmit = ({ projectType, phase }: ProjectTypeFormData) => {
    const params: UpdateProjectTypeAndPhaseParams = {
      projectType: { id, ...projectType }
    }

    if (selectedPhase?.id) {
      params.phase = phase
    }

    updateProjectTypeAndPhase(params)
  }

  const handleDeletePhase = () => {
    if (selectedPhase) {
      modal.confirm({
        centered: true,
        title: t("Confirm deleting"),
        content: t(
          "Are you sure you want to delete this milestone? This action can NOT be undone."
        ),

        cancelText: t("Cancel"),
        okText: t("Delete"),
        okButtonProps: { danger: true, ghost: true },
        onOk() {
          deletePhase(selectedPhase.id)
        }
      })
    }
  }

  const handleSelectPhase = (phase: ProjectTypeTemplatePhase) => {
    setProp({ key: "selectedPhase", value: phase })
  }

  const handleAddPhase = (data: ProjectTypeAddPhaseFormData) => {
    createPhase({ ...data, project_type_id: id })
  }

  const handleCopyPhase = () => {
    if (!selectedPhase?.id) return
    let customName = ""

    modal.confirm({
      centered: true,
      title: "Copy milestone",
      content: (
        <TextInput
          placeholder={"Optional custom name"}
          onChange={(e) => {
            customName = e.target.value
          }}
        />
      ),
      cancelText: "Cancel",
      okText: "Copy",
      onOk() {
        const name = customName.trim()
        copyPhase({
          id: selectedPhase.id,
          project_type_id: id,
          ...(name ? { name } : {})
        })
      }
    })
  }

  return (
    <ProjectType
      deleteAllowed={permissions?.settings_template_delete}
      createAllowed={permissions?.settings_template_create}
      selectedPhase={selectedPhase}
      saving={saving}
      projectType={{ ...(projectType || {}), phases: sortedPhases }}
      onDeleteProjectType={handleDeleteProjectType}
      onSelectPhase={handleSelectPhase}
      onSubmit={handleSubmit}
      onAddPhase={handleAddPhase}
      onCopyPhase={handleCopyPhase}
      onDeletePhase={handleDeletePhase}
    />
  )
}

const mapStateToProps = (state: RootState) => ({
  permissions: state.auth.permissions?.codes?.settings?.project_templates,
  selectedPhase: state.templates.selectedPhase,
  fetching: state.templates.fetching,
  saving: state.templates.saveFetching,
  projectType: state.templates.projectTypeDetails
})

const mapDispatchToProps = {
  setProp: setTemplatesProp,
  //
  fetchProjectType: fetchProjectTypeDetails,
  updateProjectTypeAndPhase: updateProjectTypeAndPhase,
  //
  deletePhase: deletePhaseForProjectType,
  copyPhase: copyPhaseForProjectType,
  createPhase: createPhaseForProjectType,
  deleteProjectType: deleteProjectType
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(ProjectTypePage)
